import { fill } from '@spaceship-fspl/styles';
import { createProps, createVariant } from '@spaceship-fspl/substance-style';
import React from 'react';
import styled, { css } from 'styled-components';

export type LogoStackedVariant = 'light' | 'dark';
export type LogoStackedSize = 'sm' | 'md' | 'lg' | 'xl';

export interface LogoStackedProps {
  variant: LogoStackedVariant;
  size?: LogoStackedSize;
}

const variant = createVariant({
  light: css`
    ${fill('neutral.000')}
  `,
  dark: css`
    ${fill('neutral.100')}
  `,
});

const size = createVariant({
  sm: css`
    height: 16px;
    width: 16px;
  `,
  md: css`
    height: 24px;
    width: 24px;
  `,
  lg: css`
    height: 32px;
    width: 32px;
  `,
  xl: css`
    height: 100px;
    width: 100px;
  `,
});

const SVG = styled.svg`
  vertical-align: middle;
  ${createProps({
    size,
  })}
`;

const Path = styled.path`
  ${createProps({
    variant,
  })}
`;

export const LogoStacked: React.FC<
  React.PropsWithChildren<LogoStackedProps>
> = ({ variant, size = 'lg' }) => (
  <SVG size={size} viewBox="0 0 240 240" xmlns="http://www.w3.org/2000/svg">
    <Path
      variant={variant}
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0 234V230.775C0 230.057 0.576867 229.485 1.30259 229.485H13.3422C14.1982 229.485 14.8867 228.785 14.8867 227.901V224.971L2.4005 220.88C1.06069 220.438 0.0186039 219.001 0.0186039 217.6V213.768C0.0186039 210.377 2.6424 208.018 6.4199 208.018H18.8689V211.243C18.8689 211.962 18.3107 212.533 17.585 212.533H6.4199C5.63835 212.533 4.78237 212.957 4.78237 213.897V217.029L17.25 221.101C18.534 221.525 19.5388 222.981 19.5388 224.418V227.882C19.5388 231.365 16.8778 234 13.3236 234H0ZM27.9126 234V210.746C27.9126 209.235 29.1594 208.018 30.7039 208.018H40.9757C44.5486 208.018 47.4515 210.875 47.4515 214.376V217.563C47.4515 221.083 44.5486 223.921 40.9757 223.921H33.7929C33.4393 223.921 33.0858 223.865 32.7694 223.755V232.71C32.7694 233.41 32.174 234 31.4668 234H27.9126ZM41.3293 219.074C42.2411 219.074 42.9854 218.393 42.9854 217.545V213.97C42.9854 213.123 42.2411 212.441 41.3293 212.441H32.9369V219.074H41.3293ZM74.8244 234C74.1545 234 73.5777 233.576 73.4102 232.95L71.4563 227.348H60.2913L58.3188 232.987C58.1513 233.558 57.5558 233.982 56.9045 233.982H53.034L61.3519 210.377C61.873 208.958 63.25 208 64.7387 208H68.5906C69.0744 208 69.5397 208.276 69.763 208.7L78.7136 234H74.8244ZM69.7816 222.944L66.246 212.441H65.483L61.966 222.944H69.7816ZM92.335 234C88.818 234 85.9709 231.144 85.9709 227.643V214.376C85.9709 210.875 88.818 208.018 92.335 208.018H103.835V211.243C103.835 211.943 103.239 212.533 102.551 212.533H92.3163C91.4418 212.533 90.716 213.233 90.716 214.118V227.919C90.716 228.785 91.4231 229.504 92.3163 229.504H102.551C103.239 229.504 103.835 230.094 103.835 230.794V234H92.335ZM119.243 234C115.67 234 112.767 231.144 112.767 227.643V214.376C112.767 210.875 115.67 208.018 119.243 208.018H131.189V211.243C131.189 211.943 130.575 212.533 129.887 212.533H119.243C118.35 212.533 117.624 213.233 117.624 214.118V218.816C117.866 218.78 118.108 218.743 118.35 218.743H130.036V221.967C130.036 222.668 129.44 223.257 128.733 223.257H117.605V227.919C117.605 228.785 118.331 229.504 119.224 229.504H129.868C130.575 229.504 131.171 230.094 131.171 230.794V234H119.243ZM140.121 234V230.775C140.121 230.057 140.698 229.485 141.424 229.485H153.445C154.32 229.485 154.99 228.785 154.99 227.901V224.971L142.522 220.88C141.182 220.438 140.14 219.001 140.14 217.6V213.768C140.14 210.377 142.764 208.018 146.541 208.018H158.99V211.243C158.99 211.962 158.413 212.533 157.688 212.533H146.523C145.723 212.533 144.904 212.957 144.904 213.897V217.029L157.371 221.101C158.674 221.544 159.66 222.962 159.66 224.418V227.882C159.66 231.365 156.999 234 153.445 234H140.121ZM184.633 234C183.926 234 183.33 233.41 183.33 232.71V223.239H173.393V232.71C173.393 233.41 172.798 234 172.091 234H168.592V208.018H172.091C172.798 208.018 173.393 208.608 173.393 209.308V218.743H183.33V209.308C183.33 208.608 183.926 208.018 184.633 208.018H188.131V234H184.633ZM198.422 234C197.696 234 197.063 233.41 197.063 232.71V208.018H200.729C201.473 208.018 202.087 208.608 202.087 209.308V234H198.422ZM210.461 234V210.746C210.461 209.235 211.708 208.018 213.252 208.018H223.524C227.097 208.018 230 210.875 230 214.376V217.563C230 221.083 227.097 223.921 223.524 223.921H216.323C215.969 223.921 215.616 223.865 215.299 223.755V232.71C215.299 233.41 214.704 234 213.978 234H210.461ZM223.878 219.074C224.79 219.074 225.534 218.393 225.534 217.545V213.97C225.534 213.123 224.79 212.441 223.878 212.441H215.485V219.074H223.878Z"
    />
    <Path
      variant={variant}
      fillRule="evenodd"
      clipRule="evenodd"
      d="M205 29C205 29 199.233 39.7333 183.433 56.2333C188.933 67.8333 192.033 80.8 192.033 94.5C192.033 138.533 160.333 175.067 118.8 182C143.133 173.033 160.8 150.2 148.733 128.767C140.033 113.267 117.067 114.867 93.1667 125.1C41.0333 147.467 9 157.633 9 157.633L205 29ZM212.133 24.9C208.633 19.4667 204.533 14.6667 200 10.5333C202.067 11 216.233 14.1 216.333 14.0333C228.267 6.1 239 7.03333 239 7.03333C239 7.03333 235.533 17.4333 223.6 25.3667C223.6 25.3667 220.6 39.9333 220.167 42C218.267 36.1 215.6 30.3667 212.133 24.9ZM60 91.2667C40.7 94.5667 33.5333 86.4 30.6333 77.8667C25.7333 63.7 31.6667 40.2667 50.8 24.6C65.3667 12.6333 84.2667 6 104.5 6C132.267 6 156.967 19.0667 173 39.4C147.333 48.3 86.7333 86.7 60 91.2667Z"
    />
  </SVG>
);
